import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import { espaceMiseAJourSchema, type EspaceMiseAJour } from '@artosera/shared';
import { accrochageApi, useEspace, type EspacePresente } from '../../api/accrochage.js';
import { useLieux } from '../../api/lieux.js';
import { useMutation } from '../../api/mutation.js';
import { dansLaPortee } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import styles from './Accrochage.module.css';

export function EspaceDetailPage() {
  const { id = '' } = useParams();
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const fiche = useEspace(galerieSlug, id);
  if (fiche.isPending) return <p role="status">{t('commun:chargement')}</p>;
  if (!fiche.data) return <p role="alert">{t('commun:erreurReseau')}</p>;
  return <EspaceFormulaire key={id} initial={fiche.data.espace} />;
}
function EspaceFormulaire({ initial }: { initial: EspacePresente }) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [espace, setEspace] = useState<EspaceMiseAJour>({
    nom: initial.nom,
    nature: initial.nature,
    lieuId: initial.lieuId,
    murs: initial.murs,
    revision: initial.revision,
  });
  const [erreurs, setErreurs] = useState<Record<string, string>>({});
  const lieux = useLieux(galerieSlug);
  const enregistrer = useMutation(
    dansLaPortee({
      mutationFn: (corps: EspaceMiseAJour, appel) =>
        accrochageApi.enregistrerEspace(galerieSlug, initial.id, corps, appel),
      onSuccess: ({ espace: sauve }) =>
        setEspace((e) => ({ ...e, revision: sauve.revision, murs: sauve.murs })),
    }),
  );
  const peutEcrire = galerieALaction(galerie, 'exhibitions:write');
  const modifierMur = (indice: number, changement: Partial<EspaceMiseAJour['murs'][number]>) =>
    setEspace({
      ...espace,
      murs: espace.murs.map((m, i) => (i === indice ? { ...m, ...changement } : m)),
    });
  function ordonnerMur(indice: number, direction: number) {
    const murs = [...espace.murs];
    const [mur] = murs.splice(indice, 1);
    if (!mur) return;
    murs.splice(indice + direction, 0, mur);
    setEspace({ ...espace, murs: murs.map((m, ordre) => ({ ...m, ordre })) });
  }
  const champ = (
    chemin: string,
    libelle: string,
    valeur: string | number,
    changer: (v: string) => void,
    nombre = false,
  ) => (
    <Field
      htmlFor={`acc-${chemin}`}
      label={t(`accrochage.${libelle}`)}
      error={erreurs[chemin] ?? erreurs[chemin.replace(/\.[^.]+$/, '')]}
    >
      <Input
        id={`acc-${chemin}`}
        type={nombre ? 'number' : 'text'}
        step={nombre ? '0.1' : undefined}
        value={valeur}
        onChange={(e) => changer(e.target.value)}
      />
    </Field>
  );
  const details = estApiError(enregistrer.error)
    ? (enregistrer.error.details as {
        raison?: string;
        plans?: { id: string; nom: string }[];
      } | null)
    : null;
  const messageErreur =
    enregistrer.error instanceof Error ? enregistrer.error.message : t('commun:erreurReseau');
  function soumettre() {
    const resultat = espaceMiseAJourSchema.safeParse(espace);
    if (!resultat.success) {
      setErreurs(
        Object.fromEntries(
          resultat.error.issues.map((e) => [
            e.path.join('.'),
            i18n.language === 'en' ? t('accrochage.champInvalide') : e.message,
          ]),
        ),
      );
      return;
    }
    setErreurs({});
    enregistrer.mutate(resultat.data);
  }
  return (
    <section className={styles.page} data-ui="page">
      <Link to={`/${galerieSlug}/accrochage`}>{t('accrochage.retour')}</Link>
      <h1>{initial.nom}</h1>
      <form
        className={styles.form}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          soumettre();
        }}
      >
        <fieldset disabled={!peutEcrire || enregistrer.isPending} className={styles.form}>
          <legend>{t('accrochage.espace')}</legend>
          {champ('nom', 'nom', espace.nom, (nom) => setEspace({ ...espace, nom }))}
          <Field htmlFor="acc-nature" label={t('accrochage.nature')}>
            <select
              id="acc-nature"
              value={espace.nature}
              onChange={(e) =>
                setEspace({ ...espace, nature: e.target.value as EspaceMiseAJour['nature'] })
              }
            >
              {['salle', 'stand', 'hors-les-murs'].map((n) => (
                <option key={n} value={n}>
                  {t(`accrochage.natures.${n}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field htmlFor="acc-lieu" label={t('accrochage.lieu')}>
            <select
              id="acc-lieu"
              value={espace.lieuId ?? ''}
              onChange={(e) => setEspace({ ...espace, lieuId: e.target.value || null })}
            >
              <option value="">—</option>
              {lieux.data?.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.nom}
                </option>
              ))}
            </select>
          </Field>
          {erreurs.murs && <p role="alert">{erreurs.murs}</p>}
          {espace.murs.map((mur, indice) => (
            <fieldset className={styles.panel} key={mur._id ?? indice}>
              <legend>
                {t('accrochage.mur')} {indice + 1}
              </legend>
              <div className={styles.actions}>
                <Button
                  type="button"
                  disabled={indice === 0}
                  onClick={() => ordonnerMur(indice, -1)}
                >
                  {t('accrochage.monterMur', { nom: mur.nom })}
                </Button>
                <Button
                  type="button"
                  disabled={indice === espace.murs.length - 1}
                  onClick={() => ordonnerMur(indice, 1)}
                >
                  {t('accrochage.descendreMur', { nom: mur.nom })}
                </Button>
              </div>
              <div className={styles.grid}>
                {champ(`murs.${indice}.nom`, 'nom', mur.nom, (nom) => modifierMur(indice, { nom }))}
                {(['largeurCm', 'hauteurCm', 'ordre'] as const).map((cle) => (
                  <div key={cle}>
                    {champ(
                      `murs.${indice}.${cle}`,
                      cle,
                      mur[cle],
                      (v) => modifierMur(indice, { [cle]: Number(v) }),
                      true,
                    )}
                  </div>
                ))}
              </div>
              <Button
                type="button"
                onClick={() =>
                  setEspace({ ...espace, murs: espace.murs.filter((_, i) => i !== indice) })
                }
              >
                {t('accrochage.retirerMur')}
              </Button>
              <svg
                className={styles.apercu}
                role="img"
                aria-label={t('accrochage.apercuMur', { nom: mur.nom })}
                viewBox={`0 0 ${Math.max(1, mur.largeurCm)} ${Math.max(1, mur.hauteurCm)}`}
              >
                <rect
                  width={mur.largeurCm}
                  height={mur.hauteurCm}
                  fill="var(--surface)"
                  stroke="var(--ink)"
                />
                {mur.zones.map((z, i) => (
                  <rect
                    key={z._id ?? i}
                    x={z.xCm}
                    y={mur.hauteurCm - z.yCm - z.hauteurCm}
                    width={Math.max(0, z.largeurCm)}
                    height={Math.max(0, z.hauteurCm)}
                    fill="var(--ink-soft)"
                    opacity="0.4"
                  />
                ))}
              </svg>
              {mur.zones.map((zone, zi) => {
                const modifierZone = (changement: Partial<typeof zone>) =>
                  modifierMur(indice, {
                    zones: mur.zones.map((z, i) => (i === zi ? { ...z, ...changement } : z)),
                  });
                const chemin = `murs.${indice}.zones.${zi}`;
                return (
                  <fieldset key={zone._id ?? zi} className={styles.form}>
                    <legend>
                      {t('accrochage.zone')} {zi + 1}
                    </legend>
                    {erreurs[chemin] && <p role="alert">{erreurs[chemin]}</p>}
                    <Field htmlFor={`acc-${chemin}-nature`} label={t('accrochage.nature')}>
                      <select
                        id={`acc-${chemin}-nature`}
                        value={zone.nature}
                        onChange={(e) =>
                          modifierZone({ nature: e.target.value as typeof zone.nature })
                        }
                      >
                        {['porte', 'fenetre', 'radiateur', 'prise', 'autre'].map((n) => (
                          <option key={n} value={n}>
                            {t(`accrochage.zones.${n}`)}
                          </option>
                        ))}
                      </select>
                    </Field>
                    {champ(`${chemin}.libelle`, 'libelle', zone.libelle ?? '', (libelle) =>
                      modifierZone({ libelle }),
                    )}
                    <div className={styles.grid}>
                      {(['xCm', 'yCm', 'largeurCm', 'hauteurCm'] as const).map((cle) => (
                        <div key={cle}>
                          {champ(
                            `${chemin}.${cle}`,
                            cle,
                            zone[cle],
                            (v) => modifierZone({ [cle]: Number(v) }),
                            true,
                          )}
                        </div>
                      ))}
                    </div>
                    <Button
                      type="button"
                      onClick={() =>
                        modifierMur(indice, { zones: mur.zones.filter((_, i) => i !== zi) })
                      }
                    >
                      {t('accrochage.retirerZone')}
                    </Button>
                  </fieldset>
                );
              })}
              <Button
                type="button"
                onClick={() =>
                  modifierMur(indice, {
                    zones: [
                      ...mur.zones,
                      { nature: 'autre', xCm: 0, yCm: 0, largeurCm: 50, hauteurCm: 50 },
                    ],
                  })
                }
              >
                {t('accrochage.ajouterZone')}
              </Button>
            </fieldset>
          ))}
          <Button
            type="button"
            onClick={() =>
              setEspace({
                ...espace,
                murs: [
                  ...espace.murs,
                  {
                    nom: t('accrochage.nouveauMur', { numero: espace.murs.length + 1 }),
                    ordre: espace.murs.length,
                    largeurCm: 400,
                    hauteurCm: 300,
                    zones: [],
                  },
                ],
              })
            }
          >
            {t('accrochage.ajouterMur')}
          </Button>
        </fieldset>
        {peutEcrire && (
          <Button type="submit" loading={enregistrer.isPending}>
            {t('accrochage.enregistrer')}
          </Button>
        )}
      </form>
      {enregistrer.isError && (
        <div role="alert">
          {details?.raison === 'mur_utilise' ? (
            <>
              <p>{t('accrochage.murUtilise')}</p>
              {details.plans?.map((p) => (
                <Link key={p.id} to={`/${galerieSlug}/accrochage/plans/${p.id}`}>
                  {p.nom}
                </Link>
              ))}
            </>
          ) : (
            messageErreur
          )}
        </div>
      )}
      {enregistrer.isSuccess && <p role="status">{t('accrochage.enregistre')}</p>}
    </section>
  );
}
